/** @jsx CanvaSX */
import { fabric } from 'fabric';
import {
  type AttachmentPoint,
  type IconData,
  type Connectable,
  type HasDraggableItems,
  type Resizable,
} from '../types';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { type MountedObjects } from '@lib/canvasx/canvasx';
import CanvaSXBehaviors from './CanvaSXBehaviors';
import CollaborationBehaviors from '../objects/CollaborationBehaviors';
import ConnectorAttachmentBehaviors from './ConnectorAttachmentBehaviors';
import FrameMemberBehaviors from '../objects/FrameMemberBehaviors';
import LockBehaviors from '../objects/LockBehaviors';
import ReadOnlyBehaviors from '../objects/ReadOnlyBehaviors';
import SmartGuideBehaviors from '../objects/SmartGuideBehaviors';
import type ItemCollection from '../models/ItemCollection';
import type WireframeBase from '../wireframes/WireframeBase';
import type WireframeItem from '../wireframes/WireframeItem';
import wireframes from '../wireframes';
import StyleCopyPasteBehaviors from './StyleCopyPasteBehaviors';
import { type RenderedDimensions } from '@lib/canvasx/components/FlexRow';
import AnalyticsBehaviors from './AnalyticsBehaviors';
import { pointInRect, pointNearLine } from '../utils/geometry';
import ItemDragBehaviors from '../objects/ItemDragBehaviors';
import DragReorderControl from '../controls/DragReorderControl';
import type DraggableItem from '../DraggableItem';
import VotingBehaviors from './VotingBehaviors';
import ShapePersistentControlBehaviors from './ShapePersistentControlBehaviors';
import { omit } from 'lodash';
import LinkToUrlBehaviors, {
  type LinkToUrlBehaviorsInterface,
} from './LinkToUrlBehaviors';
import { standardInboundConnectControlDefinition } from '../utils/connectors';

export const DEFAULT_WIDTH = 100;
export const DEFAULT_HEIGHT = 33;
const SELECTION_DISTANCE = 25;

interface Wireframe<W extends WireframeBase = WireframeBase>
  extends fabric.Object,
    ItemDragBehaviors,
    LockBehaviors,
    CanvaSXBehaviors,
    ReadOnlyBehaviors,
    SmartGuideBehaviors,
    CollaborationBehaviors,
    FrameMemberBehaviors,
    LinkToUrlBehaviorsInterface,
    VotingBehaviors,
    ShapePersistentControlBehaviors,
    ConnectorAttachmentBehaviors {
  wireframe: W;
}

@ItemDragBehaviors
@AnalyticsBehaviors
@LockBehaviors
@CanvaSXBehaviors
@ReadOnlyBehaviors
@SmartGuideBehaviors
@CollaborationBehaviors
@FrameMemberBehaviors
@LinkToUrlBehaviors
@VotingBehaviors
@ShapePersistentControlBehaviors
@ConnectorAttachmentBehaviors
class Wireframe<W extends WireframeBase = WireframeBase>
  extends StyleCopyPasteBehaviors(fabric.Object)
  implements Connectable, HasDraggableItems<WireframeItem>, Resizable
{
  type = 'wireframe';
  noScaleCache = false;
  // So edges are sharp on large objects at high scales
  objectCaching = false;

  strokeUniform = true;
  transparentCorners = false;
  strokeLineCap = 'butt';
  strokeLineJoin = 'miter';
  strokeMiterLimit = 1000;
  minScaleLimit = 0.05;
  snapAngle = 45;
  snapThreshold = 5;
  borderOpacityWhenMoving = 0;
  mountedObjects: MountedObjects = {};
  uniformScaling = true;

  // Items within the component, e.g., nav menu item in a nav menu. These are
  // additional text and icon fields. A component can have multiple items, and
  // often more can be added through various tools/controls (e.g., ToolWireframeAddItem)
  items: ItemCollection<WireframeItem>;

  // Whether the user has explicitly set a given dimension. This is used for
  // resizable components
  setDimension: boolean;
  borderRadius: number;
  textColor: string;

  // Similar to shapeName, this indicates which component this wireframe is
  wireframeName: string;

  editingItem: string;

  wireframe: W;

  // Component style (filled, round, etc.). Originally, componentState and style
  // were the same thing
  style: string;

  // Component specific state like toggle on/off, checkbox checked/unchecked, etc.
  componentState: string;

  // Default/disabled state of the component
  uiState: string;

  // For components that have different orientations, e.g. nav menus
  layout: 'column' | 'row';
  fontSize: number;

  // slider keys
  progress: number;
  sliderValue: string;

  // Some components have a label that differs from placeholder in how it is
  // handled. Certain tools, (e.g., ToolWireframeLabelPosition) affect labels
  // specifically
  label: { value: string; position: string; dimensions?: RenderedDimensions };

  // For components with a static placeholder field, like buttons. This is
  // essentially the default text field for a component. It differs from items,
  // in that there is only a single placeholder
  placeholder: {
    value: string;
    state: string;
    iconLeft: IconData;
    iconRight: IconData;
    dimensions: RenderedDimensions;
  };

  // pagination button text
  buttonLeft: string;
  buttonRight: string;

  // config options
  showBackground: boolean;
  showOutline: boolean;
  showIcons: boolean;
  showSeparators: boolean;
  showButtonText: boolean;
  showEllipses: boolean;
  showStepText: boolean;
  showStepNumbers: boolean;

  /** @deprecated use showIcons */
  icons: boolean;
  /** @deprecated  use showSeparators */
  separators: boolean;

  serializedKeys = [
    'z',
    'top',
    'left',
    'width',
    'height',
    'scaleX',
    'scaleY',
    'textContent',
    'container',
    'layoutPosition',
    'layoutManual',
    'iconLeft',
    'iconRight',
    'wireframeName',
    'fill',
    'itemsData',
    'borderRadius',
    'textColor',
    'style',
    'componentState',
    'uiState',
    'fontSize',
    'label',
    'placeholder',
    'setDimension',

    // nav menu specific keys
    'layout', // 'column' | 'row',

    'showBackground',
    'showOutline',
    'showIcons',
    'showSeparators',
    'showStepText',
    'showStepNumbers',

    // pagination
    'showButtonText',
    'showEllipses',
    'buttonLeft',
    'buttonRight',

    // slider
    'progress',
    'sliderValue',
  ];

  constructor(options) {
    super({});

    this.initializeConnectorAttachments();

    this.handleDataMigrations(options);

    super.setOptions({ ...options });
    this.initializeWireframe(options.wireframeName);

    this.initializeLockBehaviors(options.locked);
    this.initializeSmartGuides();
    this.initializeFrameMember();

    this.on('mousemove', eventData => {
      if (
        this.canvas?.getActiveObject() === this &&
        this.wireframe?.hasDraggableItems
      ) {
        const pointer = this.canvas.getPointer(eventData.e);
        this.showItemDragControls(pointer);
      }
    });
  }

  // Some cleanup to the data model for wireframes as patterns solidified
  handleDataMigrations(options) {
    // Rename 'state' to 'style'. Originally, the 'style' field was called `state`,
    // but the more accurate and clearer name is `style`.
    if (options.state) {
      options.style = options.state;
      options.state = null;
    }

    // Backwards compat for changing label 'state' field name to 'position'
    if (options.label?.state) {
      options.label.position = options.label.state;
      options.label.state = null;
    }
  }

  initializeWireframe(wireframeName: string) {
    const WireframeClass = wireframes.wireframeNameToClass(wireframeName);
    // @ts-expect-error we assume the class matches the given wireframeName
    this.wireframe = new WireframeClass(this);
    const constructor = this.wireframe.constructor as typeof WireframeClass;
    this.controls = constructor.wireframeControls;

    this.set({
      width: this.width || constructor.defaultWidth(),
      height: this.height || constructor.defaultHeight(),
    });

    // Update connector attachments to move them to the right place.
    this.synchronize();
  }

  static possibleAttachmentPoints = Object.keys(
    Wireframe.connectControlDefinition()
  ) as AttachmentPoint[];

  attachmentPoints(): AttachmentPoint[] {
    // @ts-expect-error TS doesn't like accessing constructor like this
    return this.constructor.possibleAttachmentPoints;
  }

  connectControlDefinition() {
    // @ts-expect-error TS doesn't like accessing constructor like this
    return this.constructor.connectControlDefinition();
  }

  static connectControlDefinition() {
    return standardInboundConnectControlDefinition();
  }

  get itemBorderRadius() {
    return this.wireframe.itemBorderRadius ?? 0;
  }

  itemCount() {
    return this.items?.items.length || 0;
  }

  get votingResultIcon() {
    return (this.wireframe.constructor as typeof WireframeBase).renderIcon();
  }

  get votingResultSummary() {
    return (this.wireframe.constructor as typeof WireframeBase).externalName();
  }

  // Offset from top/left for items inside components, for e.g. dropdown, which
  // has a label and placeholder before the list of items
  componentOffset() {
    return this.wireframe.componentOffset();
  }

  // Returns the Rect + id of each draggable item in an array, in canvas space.
  // Component must update itemDimensions for each item
  draggableItems({ draggingVertical, scaled = false }) {
    const items = this.items?.items;
    const rects = Array<DraggableItem<WireframeItem>>();
    const scaleX = scaled ? this.scaleX : 1;
    const scaleY = scaled ? this.scaleY : 1;
    if (items?.length > 0) {
      const offset = this.wireframe.componentOffset();
      let start = draggingVertical ? this.top : this.left;
      if (offset) {
        start += draggingVertical ? offset.y : offset.x;
      }
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const dimensions = item.containerDimensions ?? item.dimensions;
        if (dimensions) {
          const separatorOffset = this.wireframe.itemSeparatorSize;
          const width =
            (draggingVertical ? this.width : dimensions.width) * scaleX;
          const height =
            (draggingVertical ? dimensions.height : this.height) * scaleY;
          if (i > 0) {
            // Ensure that we don't overlap the separator
            draggingVertical
              ? (start += separatorOffset * scaleY)
              : (start += separatorOffset * scaleX);
          }
          rects.push({
            item,
            left: draggingVertical ? this.left : start,
            top: draggingVertical ? start : this.top,
            width,
            height,
            id: item.id,
          });
          start += draggingVertical ? height : width;
        }
      }
    }
    return rects;
  }

  draggableItemForId({ id, draggingVertical, scaled = false }) {
    const rects = this.draggableItems({ draggingVertical, scaled });
    return rects.find(rect => rect.id === id);
  }

  updateItemOrder({ id }) {
    if (
      !this.dragState.placement ||
      !id ||
      this.dragState.placement.target?.id === id
    ) {
      return false;
    }

    const item = this.items.findById(id);
    if (item) {
      this.items.placeItem(item, this.dragState.placement);
      return true;
    }

    return false;
  }

  containsPoint(
    point: fabric.Point,
    lines?: any,
    absolute?: boolean | undefined,
    calculate?: boolean | undefined
  ): boolean {
    const contains = super.containsPoint(point, lines, absolute, calculate);

    // Only perform this check if the wireframe component has draggable items
    if (this.wireframe.hasDraggableItems) {
      const adjustedPointer = this.canvas.restorePointerVpt(point);
      const zoom = this.canvas?.getZoom() || 1;
      const radius = SELECTION_DISTANCE / zoom;

      const coords = this.getCoords(true);

      let showDragControls = pointNearLine(
        adjustedPointer,
        coords[0],
        coords[1],
        radius
      );
      showDragControls ||= pointNearLine(
        adjustedPointer,
        coords[1],
        coords[2],
        radius
      );
      showDragControls ||= pointNearLine(
        adjustedPointer,
        coords[2],
        coords[3],
        radius
      );
      showDragControls ||= pointNearLine(
        adjustedPointer,
        coords[3],
        coords[0],
        radius
      );
      if (showDragControls && this.canvas.getActiveObject() === this) {
        this.showItemDragControls(adjustedPointer);
      }
    }

    return contains;
  }

  hideItemDragControls() {
    // Hide all vector drag controls
    for (const [key, value] of Object.entries(this.controls)) {
      if (key.includes('drag')) {
        value.setVisibility(false);
      }
    }
  }

  private showItemDragControls(pointer) {
    if (this.items?.items.length === 0) return;
    // Avoid changing drag control visibility while dragging
    if (this.__corner?.toString().includes('drag')) return;
    const zoom = this.canvas.getZoom();
    const radius = SELECTION_DISTANCE / zoom;
    for (const [key, value] of Object.entries(this.controls)) {
      // Hide all vector drag controls
      if (key.includes('drag')) {
        value.setVisibility(false);
      }

      const rects = this.draggableItems({
        draggingVertical: this.wireframe.componentLayout === 'column',
        scaled: true,
      });
      if (pointer.x < this.left) {
        pointer.x += radius;
      } else if (pointer.x > this.left + this.getScaledWidth()) {
        pointer.x -= radius;
      }
      if (pointer.y < this.top) {
        pointer.y += radius;
      } else if (pointer.y > this.top + this.getScaledHeight()) {
        pointer.y -= radius;
      }

      const index = rects.findIndex(rect => pointInRect(pointer, rect));
      if (index > -1) {
        const item = this.items.items[index];
        const dragKey = `drag_${item.id}`;
        if (!this[dragKey]) {
          this.controls[dragKey] = new DragReorderControl({
            id: item.id,
            draggingVertical: this.wireframe.componentLayout === 'column',
            leftTop: false,
          });
        }

        this.controls[dragKey].setVisibility(true);
        this.controller.canvas.requestRenderAll();
      }
    }
  }

  deleted() {
    this.connectorAttachments.forEach(attachment =>
      attachment.connector.detachObject(this)
    );
    super.deleted(this.controller);
  }

  tools() {
    const wireframeTools = this.wireframe.tools();
    return [wireframeTools];
  }

  showLockTool() {
    const hasMultipleItems = this.items?.items.length > 1;

    if (!hasMultipleItems) return true;

    return !this.wireframe.activeKey;
  }

  allowedStyleCopyPasteFields() {
    return ['scaleX', 'scaleY', 'fill', 'textColor'];
  }

  styleFlagEnabled(key) {
    return this.wireframe.styleFlagEnabled(key);
  }

  minDimension({ resizingWidth }) {
    return this.wireframe.minDimension({ resizingWidth });
  }

  maxDimension({ resizingWidth }) {
    return this.wireframe.maxDimension({ resizingWidth });
  }

  _render(ctx: CanvasRenderingContext2D) {
    this.controller.renderManager.withRecordedHTMLCanvas(this, ctx, ctx => {
      const { width, height, scaleX, scaleY } = this;

      // Reset mounted objects on render so that we don't have "ghost" interactive
      // areas
      this.mountedObjects = {};

      ctx.save();
      ctx.translate(-width / 2, -height / 2);
      ctx.scale(1 / scaleX, 1 / scaleY);
      this.wireframe.render(ctx);
      ctx.restore();
    });
  }

  _set(keyOrPath: KeyPath, value: any): fabric.Object {
    super._set(keyOrPath, value);

    if (
      keyOrPath === 'wireframeName' &&
      (!this.wireframe ||
        (this.wireframe && value !== this.wireframe.wireframeName))
    ) {
      this.initializeWireframe(value);
    }

    // The AI is attempting override the itemsData allow it
    if (keyOrPath === 'itemsData' && value && typeof value === 'object') {
      this.items?.loadCollection();
    }

    this.wireframe?.wasSet(keyOrPath, value);

    return this;
  }

  setFromRemote(key: KeyPath, value: any): void {
    super.setFromRemote(key, value);

    // Update items
    if (Array.isArray(key) && key.includes('itemsData')) {
      this.items?.handleSetFromRemote(key, value);
      this.wireframe.updateDimensions = true;
    }
  }

  labelToJSON() {
    return omit(this.label, 'dimensions');
  }

  placeholderToJSON() {
    return omit(this.placeholder, 'dimensions');
  }

  plainText(): string | undefined {
    const parts: string[] = [
      this.label?.value,
      this.placeholder?.value,
      ...this.items.items.map(item => item.value),
      this.sliderValue,
    ].filter(Boolean);

    return parts.join('\n');
  }

  /** Sets the `setDimension` property on the object, which indicates whether the user has explicitly set width and/or height. Use `userHasExplicitlySetDimensions()` to check this property. */
  setWhetherUserHasSetDimensions() {
    const minHeight = this.minDimension({ resizingWidth: false });
    const minWidth = this.minDimension({ resizingWidth: true });

    // setDimension is serialized on the object and signafies that the user
    // has explicitly set the width and/or height. This is required because
    // we always serialize the width and height, regardless of whether the user
    // has explicitly set them or not. We need a way to tell if any flex
    // container should override the the width and height, which would be when
    // they have not been explicitly set.
    this.set(
      'setDimension',
      this.width >= minWidth || this.height >= minHeight
    );
  }

  userHasExplicitlySetDimensions() {
    return Boolean(this.setDimension);
  }
}

export default Wireframe;
